import React, { useEffect, useRef, useState, useMemo } from "react"
import AvatarComponent from "./AvatarComponent"

const VideoFeed = ({
  stream,
  participantName = "You",
  isSelf = false,
  isPiP = false,
  livekitTrack = null,
  trackMetadata = null,
  isScreenSharing = false,
  isCameraOn = false,
}) => {
  const videoRef = useRef(null)
  const [hasVideo, setHasVideo] = useState(false)
  const [hasAudio, setHasAudio] = useState(false)
  const [isVideoPlaying, setIsVideoPlaying] = useState(false)

  // Generate participant initials
  const initials = useMemo(() => {
    return participantName
      .split(" ")
      .map((part) => part.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }, [participantName])

  // Generate consistent avatar color
  const avatarColor = useMemo(() => {
    const colors = [
      "bg-blue-600",
      "bg-purple-600",
      "bg-green-600",
      "bg-yellow-600",
      "bg-pink-600",
      "bg-indigo-600",
    ]
    let hash = 0
    for (let i = 0; i < participantName.length; i++) {
      hash = participantName.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
  }, [participantName])

  // Main effect: Handle stream and video element setup
  useEffect(() => {
    if (!stream || !videoRef.current) return

    console.log(`[VideoFeed:${participantName}] Initializing stream`, {
      streamId: stream.id,
      videoTracks: stream.getVideoTracks().length,
      audioTracks: stream.getAudioTracks().length,
    })

    const videoElement = videoRef.current

    // Set stream to video element
    videoElement.srcObject = stream

    // Analyze stream composition
    const videoTracks = stream.getVideoTracks()
    const audioTracks = stream.getAudioTracks()

    setHasVideo(videoTracks.length > 0)
    setHasAudio(audioTracks.length > 0)

    // Determine if video should be visually displayed
    const shouldShowVideo = videoTracks.some(
      (t) => t.enabled && t.readyState === "live"
    )
    setIsVideoPlaying(shouldShowVideo)

    // ✅ SIMPLIFIED: Single play attempt, non-blocking
    const playVideo = async () => {
      if (videoElement.paused) {
        try {
          await videoElement.play()
        } catch (err) {
          if (err.name !== "NotAllowedError") {
            console.error(`[VideoFeed:${participantName}] Play error:`, err)
          }
        }
      }
    }

    // Play immediately
    playVideo()

    // ✅ SIMPLIFIED: Minimal event handlers
    const handlePlaying = () => {
      const videoTracks = stream.getVideoTracks()
      setIsVideoPlaying(
        videoTracks.length > 0 &&
          videoTracks.some((t) => t.enabled && t.readyState === "live")
      )
    }

    const handleLoadedMetadata = () => {
      playVideo()
    }

    // ✅ ONLY ESSENTIAL LISTENERS
    videoElement.addEventListener("playing", handlePlaying)
    videoElement.addEventListener("loadedmetadata", handleLoadedMetadata)

    // ✅ SIMPLIFIED: Less aggressive track handling
    const handleTrackAdded = (event) => {
      if (event.track.kind === "video") {
        setHasVideo(true)
        setIsVideoPlaying(
          event.track.enabled && event.track.readyState === "live"
        )
      } else if (event.track.kind === "audio") {
        setHasAudio(true)
      }

      // Only play if paused - don't interrupt active playback
      if (videoElement.paused) {
        setTimeout(() => playVideo(), 50)
      }
    }

    const handleTrackRemoved = (event) => {
      const remainingVideoTracks = stream.getVideoTracks()
      const remainingAudioTracks = stream.getAudioTracks()

      setHasVideo(remainingVideoTracks.length > 0)
      setHasAudio(remainingAudioTracks.length > 0)
      setIsVideoPlaying(
        remainingVideoTracks.length > 0 &&
          remainingVideoTracks.some((t) => t.enabled && t.readyState === "live")
      )
    }

    stream.addEventListener("addtrack", handleTrackAdded)
    stream.addEventListener("removetrack", handleTrackRemoved)

    // Setup track listeners for mute/unmute events
    const trackListeners = []

    const setupTrackListener = (track) => {
      const handleEnded = () => {
        if (track.kind === "video") {
          setIsVideoPlaying(false)
        }
      }

      track.addEventListener("ended", handleEnded)

      trackListeners.push(() => {
        track.removeEventListener("ended", handleEnded)
      })
    }

    // Setup listeners for all existing tracks
    stream.getTracks().forEach(setupTrackListener)

    // ✅ CRITICAL CHANGE: Single timeout instead of interval
    const initialPlayTimeout = setTimeout(() => {
      if (videoElement.paused && (hasVideo || hasAudio)) {
        playVideo()
      }
    }, 100)

    // Cleanup
    return () => {
      videoElement.removeEventListener("playing", handlePlaying)
      videoElement.removeEventListener("loadedmetadata", handleLoadedMetadata)
      stream.removeEventListener("addtrack", handleTrackAdded)
      stream.removeEventListener("removetrack", handleTrackRemoved)
      trackListeners.forEach((cleanup) => cleanup())
      clearTimeout(initialPlayTimeout) // ✅ Changed from clearInterval

      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }
  }, [stream, participantName, hasVideo, hasAudio])

  // No stream fallback
  if (!stream) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-800 rounded-lg">
        <div className="flex flex-col items-center">
          <div
            className={`${avatarColor} h-24 w-24 rounded-full flex items-center justify-center text-white text-3xl font-medium`}
          >
            {initials}
          </div>
          <p className="mt-3 text-gray-300">{participantName}</p>
          <p className="mt-1 text-xs text-gray-400">No signal</p>
        </div>
      </div>
    )
  }

  // Determine what to show
  const hasActiveVideo =
    stream &&
    stream.getVideoTracks().some((t) => t.enabled && t.readyState === "live")
  const showAvatar = !hasActiveVideo
  const showScreenShareMessage =
    isScreenSharing &&
    (participantName === "You" || participantName === "Your screen")

  return (
    <div className="relative h-full w-full flex flex-col">
      {/* 
        CRITICAL: Video element is ALWAYS rendered and ALWAYS playing
        This ensures audio works even when camera is off
        We use opacity and position to hide it visually when needed
      */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isSelf}
        className="w-full h-full object-contain rounded-lg"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: isVideoPlaying ? 1 : 0,
          zIndex: 1,
          transition: "opacity 300ms ease-in-out",
          transform: isSelf && !isScreenSharing ? "scaleX(-1)" : "none",
        }}
      />

      {/* Avatar overlay when no video */}
      {showAvatar && (
        <div
          className={`absolute inset-0 flex items-center justify-center ${
            !isSelf ? "p-0" : ""
          }`}
          style={{ zIndex: 2 }}
        >
          <AvatarComponent
            initials={initials}
            stream={stream}
            text={
              isSelf
                ? "Camera off"
                : hasAudio
                  ? "Participant's camera off"
                  : "Loading..."
            }
            isPiP={isPiP}
            isSelf={isSelf}
            participantName={participantName}
          />
        </div>
      )}

      {/* Screen sharing overlay for self */}
      {showScreenShareMessage && (
        <div
          className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center"
          style={{ zIndex: 10 }}
        >
          <div className="max-w-xl text-center p-6 rounded-lg">
            <div className="flex items-center justify-center mb-4">
              <span className="text-white text-lg font-medium">
                Your screen is being shared...
              </span>
            </div>
            <p className="text-white text-xs">
              To avoid an infinity mirror, don't share your entire screen or
              browser window. Share just a tab or a different window instead.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default VideoFeed
